import { useCallback, useMemo, useState } from "react";
import type { SpeedLimitZone, Station } from "@/apis/station/station-apis";
import {
  useSpeedLimitZone,
  useStation,
  useStationStatuses,
} from "@/v2/queries";

export interface UseStationModalResult {
  isOpen: boolean;
  station: Station | null;
  speedLimitZone: SpeedLimitZone | null;
  isLoading: boolean;
  statuses: string[];
  openStation: (stationId: number) => void;
  openSpeedLimitZone: (zoneId: number) => void;
  openCreate: () => void;
  close: () => void;
  isEdit: boolean;
}

/**
 * Hook for managing station modal state and data loading.
 *
 * This hook manages two entity types (station and speed limit zone) which requires
 * custom logic beyond the standard createEntityModalHook factory.
 *
 * Handles:
 * - Modal visibility state
 * - Loading station/speed limit zone data via TanStack Query
 * - Loading statuses for dropdowns
 * - Modal opens as soon as data loads (prevents stale cached data)
 */
export function useStationModal(): UseStationModalResult {
  const [selectedStationId, setSelectedStationId] = useState<number | null>(
    null
  );
  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);

  const { data: station, isLoading: isLoadingStation } =
    useStation(selectedStationId);
  const { data: speedLimitZone, isLoading: isLoadingZone } =
    useSpeedLimitZone(selectedZoneId);
  const { data: statuses } = useStationStatuses();

  // Modal is open when:
  // 1. Create mode is active, OR
  // 2. A station was selected AND it matches the loaded data, OR
  // 3. A zone was selected AND it matches the loaded data
  const isStationReady = station != null && station.id === selectedStationId;
  const isZoneReady =
    speedLimitZone != null && speedLimitZone.id === selectedZoneId;
  const isOpen = isCreateMode || isStationReady || isZoneReady;

  const isLoading =
    (selectedStationId !== null && isLoadingStation) ||
    (selectedZoneId !== null && isLoadingZone);

  const isEdit = !isCreateMode && (isStationReady || isZoneReady);

  const openStation = useCallback((stationId: number) => {
    setSelectedStationId(stationId);
    setSelectedZoneId(null);
    setIsCreateMode(false);
  }, []);

  const openSpeedLimitZone = useCallback((zoneId: number) => {
    setSelectedZoneId(zoneId);
    setSelectedStationId(null);
    setIsCreateMode(false);
  }, []);

  const openCreate = useCallback(() => {
    setSelectedStationId(null);
    setSelectedZoneId(null);
    setIsCreateMode(true);
  }, []);

  const close = useCallback(() => {
    setSelectedStationId(null);
    setSelectedZoneId(null);
    setIsCreateMode(false);
  }, []);

  return useMemo(
    () => ({
      isOpen,
      station: station ?? null,
      speedLimitZone: speedLimitZone ?? null,
      isLoading,
      statuses: statuses ?? [],
      openStation,
      openSpeedLimitZone,
      openCreate,
      close,
      isEdit,
    }),
    [
      isOpen,
      station,
      speedLimitZone,
      isLoading,
      statuses,
      openStation,
      openSpeedLimitZone,
      openCreate,
      close,
      isEdit,
    ]
  );
}
